/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useQueueData.ts (Queues)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for fetching and managing single Queue data.
    Handles loading states and data adaptation to form state.

────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchQueueById } from '../api/queuesApi';
import { MappedFormState, QueueResponse } from '../types';
import { mapResponseToState } from '../utils/mappers';
import { getDefaultFormState } from '../utils/formDefaults';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';

/**
 * Hook for fetching Queue data
 */
export function useQueueData(
  id: string | undefined,
  duplicateId: string | null
) {
  const location = useLocation();
  const { t } = useI18n();
  const { showError } = useNotifications();
  const [mappedData, setMappedData] = useState<MappedFormState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');

  // Load existing data (edit/duplicate mode)
  useEffect(() => {
    const loadId = duplicateId || id;
    const locationKey = location.key || location.pathname + location.search;

    if (!loadId || loadId === 'new') {
      // Don't reset here - handled by the "new" state useEffect below
      return;
    }

    // Skip if already loaded for this ID and location hasn't changed
    if (lastLoadedIdRef.current === loadId && lastLocationKeyRef.current === locationKey) {
      return;
    }

    lastLoadedIdRef.current = loadId;
    lastLocationKeyRef.current = locationKey;
    setIsLoading(true);

    fetchQueueById(loadId)
      .then((queue: QueueResponse) => {
        const mapped = mapResponseToState(queue);

        // If duplicating, modify the name
        if (duplicateId) {
          mapped.formState.queueName = `${mapped.formState.queueName} (Copy)`;
        }

        setMappedData(mapped);
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : t('queues.messages.loadError');
        showError(message);
        lastLoadedIdRef.current = null;
        setMappedData(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, duplicateId, showError, t]);

  // Handle "new" state reset (including "Save and New")
  useEffect(() => {
    const loadId = duplicateId || id;
    const locationKey = location.key || location.pathname + location.search;

    if (!loadId || loadId !== 'new') {
      return;
    }

    // Check if this is a new navigation (location changed)
    const locationChanged = lastLocationKeyRef.current !== locationKey;

    // Only reset if:
    // 1. Location changed (e.g., "Save and New" navigation)
    // 2. Or we haven't processed this location yet
    if (locationChanged || lastLoadedIdRef.current !== 'new') {
      lastLoadedIdRef.current = 'new';
      lastLocationKeyRef.current = locationKey;

      // Clear sessionStorage for "Save and New"
      sessionStorage.removeItem('queues:saveAndNewData');

      // Reset to default state
      const defaultState = getDefaultFormState();
      setMappedData({
        formState: defaultState,
        tiers: [],
        extraSettings: {}
      });
    }
  }, [id, duplicateId, location]);

  return {
    mappedData,
    isLoading
  };
}

